import { useEffect, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/ui/Icon";
import { t } from "@/lib/strings";
import type { UUID } from "@/lib/types";
import type { MoveTargetParent, MoveTargetSection } from "@/components/board/MoveSubitemMenu";

/** What a copy should produce. Empty = copy in place (right below, same group). */
export interface CopyRowOpts {
  withComments?: boolean;
  /** Re-home the copy under this top-level row (sub-item copied under a parent). */
  targetParentRowId?: UUID | null;
  /** Set the copy's cell in this column, so a top-level copy lands in another group. */
  groupColumnId?: UUID | null;
  groupValue?: unknown;
}

const MENU_WIDTH = 244;

/**
 * Right-click ("context") copy menu, positioned at the cursor. Offers
 * "Duplizieren" (copy in place) plus a destination list: a top-level row copies
 * into another GROUP (the board's grouping values), a sub-item copies under
 * another PARENT. Portaled + fixed so it escapes the table's overflow, closes on
 * outside click / Escape / scroll. Lives out of the row gutter deliberately — an
 * extra gutter control crowded out the selection checkbox.
 */
export function RowContextMenu({
  x,
  y,
  isSubitem,
  parents,
  sections,
  groupColumnId,
  onCopy,
  onClose,
}: {
  x: number;
  y: number;
  isSubitem: boolean;
  parents: MoveTargetParent[];
  sections: MoveTargetSection[] | null;
  groupColumnId: UUID | null;
  onCopy: (opts: CopyRowOpts) => void;
  onClose: () => void;
}) {
  // One toggle at the top; every copy action below (in place / into group / under
  // parent) respects it — mirrors Monday's "with/without comments" choice without
  // doubling every destination row.
  const [withComments, setWithComments] = useState(false);
  useEffect(() => {
    const close = () => onClose();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  // Top-level → copy into a group (needs a grouping column). Sub-item → under a parent.
  const groupTargets = !isSubitem && groupColumnId ? sections : null;
  const parentTargets = isSubitem ? parents : [];

  // Keep the menu on-screen. Height is unknown pre-paint, so clamp the top with a
  // generous estimate; the menu also scrolls internally.
  const left = Math.min(x, window.innerWidth - MENU_WIDTH - 8);
  const estHeight = 52 + (parentTargets.length + (groupTargets?.length ?? 0)) * 32;
  const top = Math.min(y, Math.max(8, window.innerHeight - estHeight - 8));

  return createPortal(
    <>
      {/* Transparent backdrop: any click outside the menu closes it. */}
      <div
        onMouseDown={(e) => {
          e.preventDefault();
          onClose();
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
        style={{ position: "fixed", inset: 0, zIndex: 400 }}
      />
      <div
        role="menu"
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          left,
          top,
          width: MENU_WIDTH,
          maxHeight: "60vh",
          overflowY: "auto",
          zIndex: 401,
          background: "var(--surface-elev)",
          border: "1px solid var(--hairline-strong)",
          borderRadius: 10,
          boxShadow: "0 10px 28px rgba(13,62,99,0.18), 0 2px 6px rgba(13,62,99,0.06)",
          padding: 4,
        }}
      >
        {/* Comments toggle: click flips it and KEEPS the menu open (the container
            swallows the backdrop mousedown, and we never call onClose here). */}
        <button
          type="button"
          role="menuitemcheckbox"
          aria-checked={withComments}
          style={rowStyle}
          onClick={(e) => {
            e.stopPropagation();
            setWithComments((v) => !v);
          }}
          onMouseEnter={hoverOn}
          onMouseLeave={hoverOff}
        >
          <span style={checkBoxStyle(withComments)}>
            {withComments ? <Icon name="check" size={9} color="#fff" /> : null}
          </span>
          <span style={{ marginLeft: 6 }}>{t.copyIncludeComments}</span>
        </button>
        <Divider />

        <button
          type="button"
          style={rowStyle}
          onClick={() => {
            onCopy({ withComments });
            onClose();
          }}
          onMouseEnter={hoverOn}
          onMouseLeave={hoverOff}
        >
          <Icon name="copy" size={12} color="var(--ink-3)" />
          <span style={{ marginLeft: 6 }}>{t.duplicate}</span>
        </button>

        {parentTargets.length ? (
          <>
            <Divider />
            <div style={sectionLabelStyle}>{t.rowCopyUnderEntry}</div>
            {parentTargets.map((p) => (
              <button
                key={p.id}
                type="button"
                style={rowStyle}
                onClick={() => {
                  onCopy({ withComments, targetParentRowId: p.id as UUID });
                  onClose();
                }}
                onMouseEnter={hoverOn}
                onMouseLeave={hoverOff}
              >
                <Icon name="cornerDownRight" size={12} color="var(--ink-3)" />
                <span style={{ marginLeft: 6 }}>{p.title || t.titleEmpty}</span>
              </button>
            ))}
          </>
        ) : null}

        {groupTargets && groupTargets.length ? (
          <>
            <Divider />
            <div style={sectionLabelStyle}>{t.rowCopyIntoGroup}</div>
            {groupTargets.map((s) => (
              <button
                key={`${String(s.value)}::${s.label}`}
                type="button"
                style={rowStyle}
                onClick={() => {
                  onCopy({ withComments, groupColumnId, groupValue: s.value });
                  onClose();
                }}
                onMouseEnter={hoverOn}
                onMouseLeave={hoverOff}
              >
                <span>{s.label}</span>
              </button>
            ))}
          </>
        ) : null}
      </div>
    </>,
    document.body,
  );
}

function Divider() {
  return <div style={{ height: 1, background: "var(--hairline)", margin: "4px 0" }} />;
}

function hoverOn(e: React.MouseEvent<HTMLButtonElement>) {
  e.currentTarget.style.background = "var(--surface-soft)";
}
function hoverOff(e: React.MouseEvent<HTMLButtonElement>) {
  e.currentTarget.style.background = "transparent";
}

function checkBoxStyle(on: boolean): CSSProperties {
  return {
    width: 14,
    height: 14,
    flexShrink: 0,
    borderRadius: 3,
    border: `1.5px solid ${on ? "var(--accent)" : "var(--hairline-strong)"}`,
    background: on ? "var(--accent)" : "transparent",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };
}

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  width: "100%",
  textAlign: "left",
  padding: "6px 8px",
  background: "transparent",
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
  color: "var(--ink-1)",
  fontSize: 13,
  fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
};

const sectionLabelStyle: CSSProperties = {
  padding: "4px 8px 2px",
  fontSize: 11,
  color: "var(--ink-3)",
  textTransform: "uppercase",
  letterSpacing: 0.4,
};
